import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PaymentIcon from '@mui/icons-material/Payment';
import { useAuth } from '../../contexts/AuthContext';
import { mainApi } from '../../config/apiClient';

type PaymentProvider = 'paytr' | 'parampos' | 'tacreocard' | 'moka';

interface AddPaymentIntegrationModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const PROVIDER_OPTIONS: Array<{
  value: PaymentProvider;
  label: string;
  disabled: boolean;
  hint?: string;
}> = [
  { value: 'paytr', label: 'PayTR', disabled: false },
  { value: 'moka', label: 'Moka United', disabled: false },
  { value: 'tacreocard', label: 'Tacreo Kart', disabled: false, hint: 'Sadece Tacreo' },
  { value: 'parampos', label: 'ParamPos', disabled: true, hint: 'Yakında' },
];

const AddPaymentIntegrationModal: React.FC<AddPaymentIntegrationModalProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const { branches } = useAuth();

  const [provider, setProvider] = useState<PaymentProvider>('paytr');
  const [branchId, setBranchId] = useState<string>(''); // boş => şirket geneli
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [isTestMode, setIsTestMode] = useState<boolean>(false);
  const [is3DEnabled, setIs3DEnabled] = useState<boolean>(true);

  // PayTR / Tacreo Card alanları (tacreocard: AffiliateId/Hash/BaseUrl/WalletId/BranchCode)
  const [merchantId, setMerchantId] = useState<string>('');
  const [merchantKey, setMerchantKey] = useState<string>('');
  const [merchantSalt, setMerchantSalt] = useState<string>('');
  const [walletId, setWalletId] = useState<string>('-1');
  const [paymentBranchCode, setPaymentBranchCode] = useState<string>('0');
  const [showMerchantKey, setShowMerchantKey] = useState<boolean>(false);
  const [showMerchantSalt, setShowMerchantSalt] = useState<boolean>(false);
  // PayTR kayıtlı site adresi (SettingsJson.registeredSiteUrl) — domain kilidi köprüsü.
  const [registeredSiteUrl, setRegisteredSiteUrl] = useState<string>('');

  // Moka alanları (ClientCode=DealerCode, ClientUsername=Username, ClientPassword=Password)
  const [mokaDealerCode, setMokaDealerCode] = useState<string>('');
  const [mokaUsername, setMokaUsername] = useState<string>('');
  const [mokaPassword, setMokaPassword] = useState<string>('');
  const [showMokaPassword, setShowMokaPassword] = useState<boolean>(false);

  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setProvider('paytr');
      setBranchId('');
      setIsEnabled(true);
      setIsTestMode(false);
      setIs3DEnabled(true);
      setMerchantId('');
      setMerchantKey('');
      setMerchantSalt('');
      setWalletId('-1');
      setPaymentBranchCode('0');
      setShowMerchantKey(false);
      setShowMerchantSalt(false);
      setRegisteredSiteUrl('');
      setMokaDealerCode('');
      setMokaUsername('');
      setMokaPassword('');
      setShowMokaPassword(false);
      setSaving(false);
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);

    if (provider === 'paytr') {
      if (!merchantId.trim()) {
        setError('Merchant ID zorunludur.');
        return;
      }
      if (!merchantKey.trim()) {
        setError('Merchant Key zorunludur.');
        return;
      }
      if (!merchantSalt.trim()) {
        setError('Merchant Salt zorunludur.');
        return;
      }
    }

    if (provider === 'tacreocard') {
      if (!merchantId.trim()) {
        setError('Affiliate Id zorunludur.');
        return;
      }
      if (!merchantKey.trim()) {
        setError('Hash zorunludur.');
        return;
      }
    }

    if (provider === 'moka') {
      if (!mokaDealerCode.trim()) {
        setError('DealerCode zorunludur.');
        return;
      }
      if (!mokaUsername.trim()) {
        setError('Username zorunludur.');
        return;
      }
      if (!mokaPassword.trim()) {
        setError('Password zorunludur.');
        return;
      }
    }

    setSaving(true);
    try {
      const isTacreo = provider === 'tacreocard';
      const isMoka = provider === 'moka';
      const isPaytr = provider === 'paytr';
      const payload = {
        Provider: provider,
        BranchId: branchId.trim() || null,
        IsEnabled: isEnabled,
        IsTestMode: isTestMode,
        Is3DEnabled: isTacreo ? false : is3DEnabled,
        MerchantId: isMoka ? null : (merchantId.trim() || null),
        MerchantKey: isMoka ? null : (merchantKey.trim() || null),
        MerchantSalt: isTacreo
          ? (merchantSalt.trim() || 'https://services.tacreocard.com')
          : isMoka ? null : merchantSalt.trim() || null,
        ClientCode: isTacreo ? (walletId.trim() || '-1') : isMoka ? mokaDealerCode.trim() : null,
        ClientUsername: isMoka ? mokaUsername.trim() : null,
        ClientPassword: isMoka ? mokaPassword.trim() : null,
        Guid: isTacreo ? (paymentBranchCode.trim() || '0') : null,
        // SettingsJson yalnızca PayTR'de ve kayıtlı site adresi girildiyse gönderilir.
        SettingsJson: isPaytr && registeredSiteUrl.trim()
          ? JSON.stringify({ registeredSiteUrl: registeredSiteUrl.trim() })
          : undefined,
      };

      const response = await mainApi.post('/api/paymentproviderintegration/create', payload);
      const result = response.data;

      if (result.Success === false || result.success === false) {
        throw new Error(result.Message || result.message || 'Kayıt oluşturulamadı.');
      }

      onSaved();
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        err?.message ||
        'Kayıt oluşturulurken bir hata oluştu.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Ödeme Entegrasyonu Ekle
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="provider-select-label">Sağlayıcı</InputLabel>
            <Select
              labelId="provider-select-label"
              value={provider}
              label="Sağlayıcı"
              onChange={(e) => setProvider(e.target.value as PaymentProvider)}
            >
              {PROVIDER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {opt.label}
                    {opt.hint && (
                      <Chip label={opt.hint} size="small" color="warning" variant="outlined" />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="branch-select-label">Şube (boş bırakılırsa şirket geneli)</InputLabel>
            <Select
              labelId="branch-select-label"
              value={branchId}
              label="Şube (boş bırakılırsa şirket geneli)"
              onChange={(e) => setBranchId(e.target.value as string)}
            >
              <MenuItem value="">
                <em>Şirket Geneli (tüm şubeler)</em>
              </MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }}>
            <Chip label="Durum" size="small" />
          </Divider>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                  color="success"
                />
              }
              label="Aktif"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isTestMode}
                  onChange={(e) => setIsTestMode(e.target.checked)}
                  color="warning"
                />
              }
              label="Test Modu"
            />
            {provider === 'moka' && isTestMode && (
              <Typography variant="caption" color="warning.main" sx={{ alignSelf: 'center' }}>
                Test bilgisi kullanın. Canlı DealerCode ile açık bırakılırsa Moka “bayi bulunamadı” döner.
              </Typography>
            )}
            {provider !== 'tacreocard' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={is3DEnabled}
                    onChange={(e) => setIs3DEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label="3D Secure"
              />
            )}
          </Box>

          {provider === 'paytr' && (
            <>
              <Divider sx={{ my: 1 }}>
                <Chip label="PayTR Bilgileri" color="primary" size="small" />
              </Divider>

              <TextField
                label="Merchant ID"
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="off"
              />

              <TextField
                label="Merchant Key"
                value={merchantKey}
                onChange={(e) => setMerchantKey(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="new-password"
                type={showMerchantKey ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowMerchantKey((v) => !v)}
                        edge="end"
                      >
                        {showMerchantKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Merchant Salt"
                value={merchantSalt}
                onChange={(e) => setMerchantSalt(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="new-password"
                type={showMerchantSalt ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowMerchantSalt((v) => !v)}
                        edge="end"
                      >
                        {showMerchantSalt ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Kayıtlı Site Adresi (opsiyonel)"
                value={registeredSiteUrl}
                onChange={(e) => setRegisteredSiteUrl(e.target.value.trim())}
                fullWidth
                size="small"
                placeholder="https://www.ornek.com"
                helperText="PayTR mağaza bilgileri yalnızca tek bir siteye tanımlıysa doldurun. Ödeme (örneğin e-posta linki) başka bir adreste başlarsa müşteri bu sitedeki güvenli ödeme sayfasına yönlendirilir, PayTR domain kilidine takılmaz. Ödeme zaten bu adreste açılıyorsa boş bırakın."
              />

              <Alert severity="info" icon={false} sx={{ fontSize: '0.85rem' }}>
                Bu bilgileri PayTR Mağaza Panelinden <strong>Mağaza Bilgileri &gt; Entegrasyon Bilgileri</strong> bölümünden alabilirsiniz.
              </Alert>
            </>
          )}

          {provider === 'tacreocard' && (
            <>
              <Divider sx={{ my: 1 }}>
                <Chip label="Tacreo Kart Bilgileri" color="warning" size="small" />
              </Divider>

              <Alert severity="warning" sx={{ fontSize: '0.85rem' }}>
                Bu ödeme yöntemi yalnızca Tacreo e-ticaret mağazası içindir. Diğer firmalar kullanmamalıdır.
              </Alert>

              <TextField
                label="Affiliate Id"
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="off"
                helperText="Tacreo Card API affiliate_id"
              />

              <TextField
                label="Hash"
                value={merchantKey}
                onChange={(e) => setMerchantKey(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="new-password"
                type={showMerchantKey ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowMerchantKey((v) => !v)}
                        edge="end"
                      >
                        {showMerchantKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="API Base URL"
                value={merchantSalt}
                onChange={(e) => setMerchantSalt(e.target.value)}
                fullWidth
                size="small"
                placeholder="https://services.tacreocard.com"
                helperText="Boş bırakılırsa varsayılan: https://services.tacreocard.com"
              />

              <FormControl fullWidth size="small">
                <InputLabel id="wallet-select-label">Cüzdan</InputLabel>
                <Select
                  labelId="wallet-select-label"
                  value={walletId}
                  label="Cüzdan"
                  onChange={(e) => setWalletId(e.target.value as string)}
                >
                  <MenuItem value="-1">Tümü (-1)</MenuItem>
                  <MenuItem value="1">Giyim (1)</MenuItem>
                  <MenuItem value="0">Restoran (0)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Branch Code"
                value={paymentBranchCode}
                onChange={(e) => setPaymentBranchCode(e.target.value)}
                fullWidth
                size="small"
                helperText="payment API branch_code (varsayılan 0)"
              />
            </>
          )}

          {provider === 'moka' && (
            <>
              <Divider sx={{ my: 1 }}>
                <Chip label="Moka United Bilgileri" color="primary" size="small" />
              </Divider>

              <TextField
                label="DealerCode (Bayi Kodu)"
                value={mokaDealerCode}
                onChange={(e) => setMokaDealerCode(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="off"
              />

              <TextField
                label="Username (API Kullanıcı Adı)"
                value={mokaUsername}
                onChange={(e) => setMokaUsername(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="off"
              />

              <TextField
                label="Password (API Şifresi)"
                value={mokaPassword}
                onChange={(e) => setMokaPassword(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="new-password"
                type={showMokaPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowMokaPassword((v) => !v)}
                        edge="end"
                      >
                        {showMokaPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Alert severity="info" icon={false} sx={{ fontSize: '0.85rem' }}>
                Bu bilgileri Moka United bayi panelinizden alabilirsiniz. Test için <strong>Test Modu</strong>'nu açık bırakın.
              </Alert>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPaymentIntegrationModal;
